import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    useNotificationSettings,
    useUpdateNotificationSettings,
} from '@/hooks/use-notification-settings-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/settings/notifications',
)({
    component: NotificationSettingsPage,
})

function NotificationSettingsPage() {
    const { data: settings, isLoading } = useNotificationSettings()
    const updateSettings = useUpdateNotificationSettings()

    const [form, setForm] = useState<{
        new_order_enabled: boolean
        overdue_enabled: boolean
        stock_low_enabled: boolean
        active_from: string
        active_to: string
    } | null>(null)

    const currentForm = form ?? {
        new_order_enabled: settings?.new_order_enabled ?? true,
        overdue_enabled: settings?.overdue_enabled ?? true,
        stock_low_enabled: settings?.stock_low_enabled ?? false,
        active_from: settings?.active_from ?? '06:00',
        active_to: settings?.active_to ?? '22:00',
    }

    const handleSave = () => {
        updateSettings.mutate(currentForm, {
            onSuccess: () => setForm(null),
        })
    }

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-3xl space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{m.notificationSettings()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="new-order">{m.newOrderEnabled()}</Label>
                        <Switch
                            id="new-order"
                            checked={currentForm.new_order_enabled}
                            onCheckedChange={(v) =>
                                setForm({
                                    ...currentForm,
                                    new_order_enabled: v,
                                })
                            }
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <Label htmlFor="overdue">{m.overdueEnabled()}</Label>
                        <Switch
                            id="overdue"
                            checked={currentForm.overdue_enabled}
                            onCheckedChange={(v) =>
                                setForm({
                                    ...currentForm,
                                    overdue_enabled: v,
                                })
                            }
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <Label htmlFor="stock-low">{m.stockLowEnabled()}</Label>
                        <Switch
                            id="stock-low"
                            checked={currentForm.stock_low_enabled}
                            onCheckedChange={(v) =>
                                setForm({
                                    ...currentForm,
                                    stock_low_enabled: v,
                                })
                            }
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="active-from">
                                {m.activeFrom()}
                            </Label>
                            <Input
                                id="active-from"
                                type="time"
                                value={currentForm.active_from}
                                onChange={(e) =>
                                    setForm({
                                        ...currentForm,
                                        active_from: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="active-to">{m.activeTo()}</Label>
                            <Input
                                id="active-to"
                                type="time"
                                value={currentForm.active_to}
                                onChange={(e) =>
                                    setForm({
                                        ...currentForm,
                                        active_to: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <Button
                        className="mt-2"
                        disabled={updateSettings.isPending}
                        onClick={handleSave}
                    >
                        {m.saveSettings()}
                    </Button>
                </CardContent>
            </Card>
        </div>
    )
}
